import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { showToast } from 'legacy/common/utils/toast';

import useEventCreation from 'legacy/events/hooks/useEventCreation';

import BasicForm from 'legacy/events/components/create-event/BasicForm';
import EventSettings from 'legacy/events/components/create-event/EventSettings';
import PricingTiers from 'legacy/events/components/create-event/settings/PricingTiers';
import TrackingLinksPage from 'legacy/events/components/create-event/TrackingLinksPage';
import DiscountCodesPage from 'legacy/events/components/create-event/DiscountCodePage';
import PublishModal from 'legacy/events/components/create-event/PublishModal';
import EventSkeleton from 'legacy/events/components/event-page/EventSkeleton';
import PublicationRequestedModal from '../components/create-event/PublicationRequestedModal';
import RequestToPublishForm from '../components/create-event/RequestToPublishForm';
import RecurringSettings from '../components/create-event/RecurringSettings';
import OffersPage from '../components/create-event/Offers';
import PrePurchaseQuestions from '../components/create-event/PrePurchaseQuestions';
import { OffersStep } from '../offers.types';
import { EventEditorProvider, Screens } from '../contexts/EventEditorContext';
import Redirect from 'legacy/components/util/Redirect/Redirect';
import { useIntercom } from 'features/intercom/context/IntercomProvider';

const EventSchema = Yup.object({
  photo: Yup.object({
    url: Yup.string().required('Required field')
  }),
  title: Yup.string().required('Required field'),
  address: Yup.string().required('Required field'),
  startDate: Yup.string().required('Required field'),
  description: Yup.string().required('Required field')
});

// Component to render based on current screen
const ScreenComponent = ({ screen }: { screen: Screens }) => {
  switch (screen) {
    case Screens.BASIC_FORM:
      return <BasicForm />;
    case Screens.EVENT_SETTINGS:
      return <EventSettings />;
    case Screens.RECURRING_SETTINGS:
      return <RecurringSettings />;
    case Screens.PRICING_TIERS:
      return <PricingTiers />;
    case Screens.REQUEST_TO_PUBLISH_FORM:
      return <RequestToPublishForm />;
    case Screens.TRACKING_LINKS:
      return <TrackingLinksPage />;
    case Screens.DISCOUNT_CODES:
      return <DiscountCodesPage />;
    case Screens.OFFERS:
      return <OffersPage />;
    case Screens.PRE_PURCHASE_QUESTIONS:
      return <PrePurchaseQuestions />;
    default:
      return <BasicForm />;
  }
};

interface CreateEventProps {
  eventId?: string;
  orgId?: string;
  startDate?: string;
}

const CreateEvent: React.FC<CreateEventProps> = ({
  eventId,
  orgId,
  startDate
}) => {
  const { t } = useTranslation(['events']);
  const router = useRouter();

  const { setHideIntercom } = useIntercom();

  useEffect(() => {
    setHideIntercom(true);

    return () => {
      setHideIntercom(false);
    };
  }, [setHideIntercom]);

  const [screen, setScreen] = useState<Screens>(Screens.BASIC_FORM);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPublicationRequestedModal, setShowPublicationRequestedModal] =
    useState(false);
  const [offersStep, setOffersStep] = useState<OffersStep>(
    OffersStep.CREATE_OFFER
  );

  const togglePublishModal = () => {
    setShowPublishModal((showPublishModal) => !showPublishModal);
  };

  const normalizedStartDate = useMemo(() => {
    if (!startDate) {
      return undefined;
    }

    const providedDate = moment(startDate);
    if (providedDate.isValid()) {
      if (
        providedDate.hour() === 0 &&
        providedDate.minute() === 0 &&
        providedDate.second() === 0
      ) {
        return providedDate
          .clone()
          .add(1, 'hours')
          .startOf('hour')
          .toISOString();
      }
      return providedDate.toISOString();
    }
    return undefined;
  }, [startDate]);

  const eventCreation = useEventCreation({
    eventId,
    orgId,
    startDate: normalizedStartDate
  });

  const onSubmit = async () => {
    if (eventCreation.shouldRequestToPublish) {
      if (
        eventCreation.requestToPublishFormId ||
        eventCreation.requestToPublishWorkflowId
      ) {
        setScreen(Screens.REQUEST_TO_PUBLISH_FORM);
      } else {
        await requestPublication();
      }
    } else {
      togglePublishModal();
    }
  };

  const requestPublication = async (data?: { [key: string]: unknown }) => {
    await eventCreation.requestPublication(data);
    setShowPublicationRequestedModal(true);
  };

  const requestPublicationWorkflow = async (data?: {
    [key: string]: unknown;
  }) => {
    await eventCreation.requestPublicationWorkflow(data);
    setShowPublicationRequestedModal(true);
  };

  if (eventCreation.shouldShowEventLoading && eventCreation.isLoadingEvent) {
    return <EventSkeleton />;
  }

  // Make sure only organizers can access the page
  if (eventCreation.event && !eventCreation?.event?.isOrganizer) {
    return <Redirect to="/404" />;
  }

  return (
    <Formik
      initialValues={eventCreation.initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validationSchema={EventSchema}
    >
      {({ values, dirty }) => {
        useEffect(() => {
          // Update values as soon as they are edited.
          if (dirty) {
            eventCreation.update(values);
          }
        }, [values, dirty]);

        const publishEvent = async () => {
          await eventCreation.publish(values);

          router.push(`/events/${eventCreation.eventId}`);
          showToast({
            icon: '🙌',
            message: t('events:createEvent.publishedSuccess')
          });
        };

        return (
          <Form>
            <EventEditorProvider
              associationId={eventCreation.associationId}
              eventId={eventCreation.eventId}
              event={eventCreation.event}
              isDraft={values.isDraft}
              isEditing={!!values._id}
              isLoading={eventCreation.isLoading}
              lastUpdated={eventCreation.lastUpdated}
              eventSeriesId={eventCreation.initialValues.seriesId}
              eventShareToken={eventCreation.initialValues.shareToken}
              shouldRequestToPublish={eventCreation.shouldRequestToPublish}
              requestToPublishFormId={eventCreation.requestToPublishFormId}
              requestToPublishWorkflowId={
                eventCreation.requestToPublishWorkflowId
              }
              startDate={normalizedStartDate}
              offersStep={offersStep}
              setOffersStep={setOffersStep}
              createDraft={eventCreation.createDraft}
              requestPublication={requestPublication}
              requestPublicationWorkflow={requestPublicationWorkflow}
              refetchEvent={eventCreation.refetchEvent}
              currentScreen={screen}
              setCurrentScreen={setScreen}
            >
              <ScreenComponent screen={screen} />
            </EventEditorProvider>

            {showPublishModal && (
              <PublishModal
                onClose={togglePublishModal}
                onPublish={publishEvent}
                isLoading={eventCreation.isLoading}
              />
            )}

            {showPublicationRequestedModal && (
              <PublicationRequestedModal eventCoverImgUrl={values.photo.url} />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateEvent;
